import { useState, useCallback } from "react";
import { subscriptionReportService } from "../../services";

export const useSubscriptionReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  /**
   * Récupère le résumé des abonnements
   * @param {Object} params - Paramètres de filtrage
   */
  const getSummary = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionReportService.getSummary(params);
      setSummary(result.data);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère la liste détaillée des abonnements
   * @param {Object} params - Paramètres incluant page, limit, filtres
   */
  const getList = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionReportService.getList(params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère les statistiques d'utilisation des services par abonnement
   * @param {Object} params - Paramètres de filtrage
   */
  const getServiceUsage = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionReportService.getServiceUsage(params);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère les abonnements qui expirent bientôt
   * @param {number} days - Nombre de jours avant expiration
   */
  const getExpiringSoon = useCallback(async (days = 7) => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionReportService.getSummary({
        expiringSoon: true,
        days,
      });
      return result.data.expiringSoon || [];
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calcule le MRR (Monthly Recurring Revenue)
   */
  const calculateMRR = useCallback((data) => {
    if (!data || !data.byPlan) return 0;

    return data.byPlan.reduce((total, plan) => {
      return total + plan.planPrice * plan.activeCount;
    }, 0);
  }, []);

  /**
   * Calcule le taux de renouvellement
   */
  const calculateRenewalRate = useCallback((data) => {
    if (!data || !data.overview) return 0;

    const { activeSubscriptions, expiredSubscriptions } = data.overview;
    const total = activeSubscriptions + expiredSubscriptions;

    return total > 0 ? (activeSubscriptions / total) * 100 : 0;
  }, []);

  /**
   * Prépare les données pour le graphique d'évolution
   */
  const prepareTimelineData = useCallback((data) => {
    if (!data || !data.byMonth) return [];

    return data.byMonth.map((month) => ({
      period: month.month,
      "Nouveaux abonnements": month.newSubscriptions,
      Revenus: month.revenue,
      Annulations: month.cancellations,
      Actifs: month.active,
    }));
  }, []);

  /**
   * Exporte les données des abonnements
   */
  const exportData = useCallback(async (format = "csv", params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await subscriptionReportService.export(format, params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    summary,
    getSummary,
    getList,
    getServiceUsage,
    getExpiringSoon,
    calculateMRR,
    calculateRenewalRate,
    prepareTimelineData,
    exportData,
  };
};
